import React, { useRef, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

interface ChatInputProps {
  initialContent?: string;
  handleChatInputChange: (content: string, fontSize: string) => void;
  placeholderText?: string;
  handleInviteToChat?: any;
  disabled?: boolean;
  handleSendMessage?: any;
}

// Toolbar Options
const TOOLBAR_OPTIONS = [
  ["bold", "italic", "underline", "strike", "link"],
  ["clean"],
];

const ChatInput: React.FC<ChatInputProps> = ({
  initialContent,
  handleChatInputChange,
  placeholderText,
  disabled,
  handleSendMessage,
}) => {
  const chatInputRef = useRef<ReactQuill | null>(null);

  useEffect(() => {
    if (initialContent && chatInputRef.current) {
      chatInputRef.current.getEditor().getText();
    }
  }, [initialContent]);

  // Handle editor change
  const handleEditorChange = (content: string) => {
    if (chatInputRef.current) {
      const editor = chatInputRef.current.getEditor();
      const selection = editor.getSelection();

      if (editor) {
        handleChatInputChange(content, "");
      }

      if (selection) {
        const format = editor.getFormat(selection);
        if (format.size) {
          handleChatInputChange(content, format.size);
        } else {
          handleChatInputChange(content, "normal");
        }
      }
    }
  };

  useEffect(() => {
    // Handle paste
    const handlePaste = (e: ClipboardEvent) => {
      e.preventDefault();
      const text = e.clipboardData.getData("text/plain");
      document.execCommand("insertHTML", false, text);
    };

    if (chatInputRef.current) {
      const chatInput = chatInputRef.current.getEditor();
      chatInput.root.addEventListener("paste", handlePaste);
    }

    return () => {
      if (chatInputRef.current) {
        const chatInput = chatInputRef.current.getEditor();
        chatInput.root.removeEventListener("paste", handlePaste);
      }
    };
  }, [chatInputRef.current]);

  // Quill editor styling
  const chatInputStyle = `
    .ql-snow .ql-toolbar {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      background-color: #fff;
      border: none;
    }
    .ql-snow .ql-picker.ql-size {
      margin-left: -14px;
      width: 70px
    }
    .ql-snow .ql-list{
      margin-left: -5px;
    }
    .ql-snow .ql-link{
      margin-left: -5px;
    }

    .ql-snow .ql-strike,
    .ql-snow .ql-blockquote,
    .ql-snow .ql-link{
      margin-left: 5px;
    }
   
    .ql-snow .ql-editor {
      min-height: 50px;
      max-height: 123px;
      overflow-y: auto;
      font-size: 16px;
    }
  `;

  return (
    <div style={{ position: "relative" }}>
      {/* Styles */}
      <style>{chatInputStyle}</style>

      {/* Editor */}
      <ReactQuill
        id="chatInput"
        theme="snow"
        ref={chatInputRef}
        defaultValue={initialContent}
        value={initialContent}
        onChange={handleEditorChange}
        modules={{
          toolbar: TOOLBAR_OPTIONS,
        }}
        placeholder={placeholderText}
        className="custom-quill-editor"
        style={{
          marginBottom: "20px",
          borderRadius: "5px",
          cursor: disabled ? "not-allowed" : "auto",
        }}
        readOnly={disabled}
        onKeyUp={(e: any) => {
          if (e.key === "Enter" && !e.shiftKey) {
            // Check if Enter is pressed without the Shift key
            e.preventDefault(); // Prevent the default action (inserting a new line)

            // Send the message
            handleSendMessage();
          }
        }}
      />
    </div>
  );
};

export default ChatInput;
